import { supabase } from './supabase';
import type { GuestInfo } from '../types/eventPlanner';

export async function getRegistryGuests(registryId: string): Promise<GuestInfo[]> {
  try {
    // Get guest tables and RSVPs for this registry
    const [guestTablesResult, rsvpResult] = await Promise.all([
      supabase
        .from('guest_tables')
        .select('guest_name, table_number')
        .eq('registry_id', registryId),
      supabase
        .from('registry_rsvps')
        .select('name, email, status')
        .eq('registry_id', registryId)
    ]);

    const guestTables = guestTablesResult.data || [];
    const rsvps = rsvpResult.data || [];

    // Combine guest data
    const combinedGuests: GuestInfo[] = guestTables.map(guest => {
      const rsvp = rsvps.find(r => r.name.toLowerCase() === guest.guest_name.toLowerCase());
      return {
        id: `guest-${guest.guest_name}`,
        name: guest.guest_name,
        email: rsvp?.email,
        tableAssignment: guest.table_number > 0 ? `Table ${guest.table_number}` : undefined,
        seatAssignment: undefined,
        rsvpStatus: (rsvp?.status as 'attending' | 'not_attending') || 'pending'
      };
    });

    return combinedGuests;
  } catch (error) {
    console.error('Error loading registry guests:', error);
    return [];
  }
}

export async function saveLayoutToRegistry(registryId: string, layout: any): Promise<void> {
  try {
    console.log('saveLayoutToRegistry - Starting save with registryId:', registryId);
    console.log('saveLayoutToRegistry - Layout data:', layout);
    
    const { supabase } = await import('./supabase');
    
    // Read existing theme so we merge instead of overwrite
    const { data: existing } = await supabase
      .from('registries')
      .select('theme')
      .eq('id', registryId)
      .maybeSingle();

    const merged = {
      ...(typeof existing?.theme === 'object' && existing.theme ? existing.theme : {}),
      layout: layout.elements,
      metadata: layout.metadata,
    };

    const { error } = await supabase
      .from('registries')
      .update({ theme: merged })
      .eq('id', registryId);

    console.log('saveLayoutToRegistry - Update error:', error);
    if (error) throw error;
    
    console.log('saveLayoutToRegistry - Save completed successfully');
  } catch (error) {
    console.error('Error saving layout:', error);
    throw error;
  }
}

export function generateLayoutExport(elements: any[], guests: GuestInfo[], metadata: any) {
  return {
    layout: elements,
    metadata: {
      ...metadata,
      exportedAt: new Date().toISOString(),
      guestCount: guests.length
    },
    guestAssignments: guests.reduce((acc, guest) => {
      acc[guest.id] = guest;
      return acc;
    }, {} as Record<string, GuestInfo>)
  };
}